import { FilterControlV2 } from '../../FilterControlV2';
import { ITableStepSubLevelEvaluator } from '../../TablaTypes';
import { TableNode, TTableIdType } from '../models/TableNode';
import { TableUtilsGeneral } from '../utils/General';

interface IBuildDataResult {
    map: Map<TTableIdType, TableNode>;
    array: Array<TableNode>;
}

type ILevelEvaluator = {
    Level: number;
} & ITableStepSubLevelEvaluator<any, any, any>

export class TableDataBuilder {
    /** Indica si los datos actuales son confiables */
    public isDataReliable: boolean = true;
    /** Indica si la tabla tiene columna de colapso */
    public hasCollapseColumn: boolean = false;
    /** Configuración de niveles de la tabla */
    private levelsConfig: Map<number, ILevelEvaluator>;
    private filter: FilterControlV2

    // constructor() { }

    public updateContext(
        levelsConfig: Map<number, ILevelEvaluator>,
        filter: FilterControlV2,
    ): void {
        this.levelsConfig = levelsConfig;
        this.filter = filter;
    }

    // ======================================================================
    //#region UPDATE DATA
    // ======================================================================

    /**
     * Punto de entrada principal para construir o reconstruir toda la estructura de datos de la tabla.
     * @param data El array de datos del nivel raíz.
     * @param lastReliableData Indica si los datos anteriores eran confiables para intentar preservar estados.
     * @param lastItemsTableMap El mapa de nodos anterior para intentar recuperar estados.
     * @returns Un objeto con el nuevo mapa y array de nodos construidos.
     */
    public buildData<TDataTable>(
        data: TDataTable[],
        lastReliableData: boolean,
        lastItemsTableMap: Map<TTableIdType, TableNode>,
    ): IBuildDataResult {

        if (!this.levelsConfig || this.levelsConfig.size === 0) {
            throw new Error("La configuración de niveles no ha sido establecida. Asegúrate de llamar a updateContext() antes de buildData().");
        }

        // reiniciar banderas globales
        this.isDataReliable = true;
        this.hasCollapseColumn = false;

        // construir árbol
        const finalMap = this.IteratorUpdateData(1, lastReliableData, data, lastItemsTableMap, null);
        const finalArray = Array.from(finalMap.values());

        return { map: finalMap, array: finalArray };
    }

    private IteratorUpdateData(
        level: number,
        reliablePastData: boolean,
        newData: any[],
        lastItemsTable: Map<TTableIdType, TableNode>,
        parentItem: TableNode | null
    ): Map<TTableIdType, TableNode> {

        const levelConfig = this.levelsConfig.get(level);
        const idMember = levelConfig.IdMember;
        const nextLevel = level + 1;

        if (!idMember) this.isDataReliable = false;

        const lastItemsTableAux = new Map(lastItemsTable);
        const newLevelMap = new Map<TTableIdType, TableNode>();

        newData.forEach((dat, i) => {
            // >> GENERACIÓN Y VALIDACIÓN DE ID
            let ID: TTableIdType = idMember ? dat[idMember] : -i;

            if (idMember) {
                if (ID === undefined) {
                    ID = `NotFoundId-${level}-${i}`;
                    this.isDataReliable = false;
                    console.warn(`Id no encontrado en nivel ${level}: `, dat);
                } else if (newLevelMap.has(ID)) {
                    this.isDataReliable = false;
                    console.warn(`ID duplicado en nivel ${level}: ${ID}`);
                }
            }

            // >> RECUPERACIÓN O CREACIÓN DEL NODO (Usando nuestra nueva clase)
            let itemResult: TableNode;

            if (reliablePastData && lastItemsTableAux.has(ID)) {
                itemResult = lastItemsTableAux.get(ID); // Pa' recuperar estado anterior
                // @ts-ignore
                itemResult.data = dat; // Actualizar data
                // @ts-ignore
                itemResult.initialPos = i;
                itemResult.itemParent = parentItem;

            } else {
                // Nodo nuevo 🔥
                itemResult = new TableNode({
                    id: ID,
                    data: dat,
                    levelRow: level,
                    initialPos: i,
                    itemParent: parentItem,
                });
            }

            // >> Actualiza configuraciones mutables
            itemResult.isRowEnable = levelConfig.OnEvalIsEnableRow
                ? levelConfig.OnEvalIsEnableRow(dat, itemResult.isRowEnable, i)
                : true;
            itemResult.isCollapsed = levelConfig.OnEvalIsCollapsedRow
                ? levelConfig.OnEvalIsCollapsedRow(dat, itemResult.isCollapsed, i)
                : (itemResult.isCollapsed != null ? itemResult.isCollapsed : true);

            // >> RECURSIVIDAD HACIA LOS HIJOS (Top-Down traversal)
            if (this.levelsConfig.has(nextLevel) && !levelConfig.ActiveDataOnlyWhenDisplayData) {
                const parentsArray = TableUtilsGeneral.getParentsDataArray(itemResult);
                const subData = this.levelsConfig.get(nextLevel).OnGetData(dat, parentsArray) || [];

                // Procesar los hijos antes de evaluar al padre
                itemResult.childDataOriginMap = this.IteratorUpdateData(
                    nextLevel,
                    reliablePastData,
                    (subData || []) as any[],
                    itemResult.childDataOriginMap,
                    itemResult
                );

                const hasFilters = this.filter._HasActiveFilters();
                // >> Filtrar los hijos para la vista inicial (se pueden perder estados de nodos no checkeados, pero es lo esperado)
                itemResult.childDataFilteredArray = (() => {
                    const newDataMap = itemResult.childDataOriginMap;
                    const lastDataFilteredMap = new Map(itemResult.childDataFilteredArray.map(node => [node.id, node]));

                    return Array.from(newDataMap.values())
                        .filter(newNode => {
                            const oldChildNode = lastDataFilteredMap.get(newNode.id);
                            if (oldChildNode?.isChecked || !hasFilters) {
                                return true; // si el nodo estaba checkeado se conserva el estado
                            }
                            // los nodos no checkeados se filtran, pueden perder sus estados
                            newNode.isChecked = false; // resetear si no estaba checheado antes
                            newNode.isIndeterminateChecked = false;
                            return this.filter._IsItemValid(newNode)
                        });
                })()

                if (itemResult.childDataOriginMap.size === 0) {
                    itemResult.isCollapsed = true;
                } else {
                    this.hasCollapseColumn = true; // si al menos un nodo tiene hijos, la columna de colapsar es necesaria
                }
            } else if (levelConfig.ActiveDataOnlyWhenDisplayData) {
                this.hasCollapseColumn = true; // Si los datos se cargan sólo al expandir, entonces la columna de colapsar es necesaria
            }

            // >> Evaluar estado del nodo basándonos en sus hijos (Bottom-Up)
            itemResult.syncCheckStateWithChildren();

            newLevelMap.set(ID, itemResult);
        });

        return newLevelMap;
    }

    // /**
    //  * Calcula el estado de un nodo recién construido o recuperado.
    //  */
    // private evaluateNodeStateBottomUp(node: TableNode, levelConfig: ILevelEvaluator) {
    //     // Si no tiene hijos o los hijos se cargan diferidos (Lazy Load)
    //     if (node.isLeaf || levelConfig.ActiveDataOnlyWhenDisplayData) {
    //         // Si es un nodo nuevo y su padre fue recuperado como "checked", el hijo hereda.
    //         // Si el nodo fue recuperado, ya tiene su estado anterior guardado.
    //         if (node.itemParent && node.itemParent.isChecked && !node.isChecked) {
    //             node.isChecked = true;
    //         }
    //         return;
    //     }

    //     node.syncCheckStateWithChildren();
    // }

    // ======================================================================
    //#region UPDATE BRANCH
    // ======================================================================

    /**
     * Actualiza solo los hijos de un nodo específico.
     * Útil para Lazy Loading o refresco de sub-ramas.
     *
     * * Lazy Loading: Cuando expandes un nodo y quieres cargar sus hijos en ese momento.
     */
    public updateBranch<TDataTable, TSubDataTable>(
        parentNode: TableNode<TDataTable>,
        newSubData: TSubDataTable[]
    ): void {
        if (!this.levelsConfig || this.levelsConfig.size === 0) {
            throw new Error("La configuración de niveles no ha sido establecida. Asegúrate de llamar a updateContext() antes de updateBranch().");
        }

        const nextLevel = parentNode.levelRow + 1;

        // >> Re-contruir solo los hijos de este nodo
        parentNode.childDataOriginMap = this.IteratorUpdateData(
            nextLevel,
            this.isDataReliable, // O la bandera que prefieras
            newSubData || [],
            parentNode.childDataOriginMap,
            parentNode
        );

        // >> Sincronizar la vista filtrada inicial de esa rama. // NOTE ESTO SE HACE EN LA TABLA POR AHORA
        // parentNode.childDataFilteredArray = Array.from(parentNode.childDataOriginMap.values());

        // >> Re-evaluar al padre y subir, el padre debe sincronizarse
        this.bubbleUpUpdate(parentNode);
    }

    /**
     * Sube recursivamente desde un nodo hasta la raíz para sincronizar estados.
     */
    private bubbleUpUpdate(node: TableNode | null): void {
        let current = node;
        while (current) {
            const hasChanged = current.syncCheckStateWithChildren();

            // Si el estado de este padre no cambió, no es necesario seguir subiendo
            if (!hasChanged) break;

            current = current.itemParent;
        }
    }
}